import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Sparkles, Crown, ArrowRight, Zap, Droplets, Sun, Activity, Search, UserRound, HelpCircle, RotateCcw, ChevronRight, ChevronLeft, CalendarClock } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

/**
 * ProviderChooserModal
 * --------------------
 * Beautiful "Who would you like to book with?" picker that fires when a
 * client taps BOOK NOW. Two large cards side-by-side:
 *
 *   • Karine Lauzon, RN — all locations, full treatment menu
 *   • Medical Aesthetician — Ottawa only, laser / skincare / hydrafacial / RF
 *
 * Picking a card calls onPick(slug) which the parent (HomePage) uses to
 * set prefillProvider on InlineBookingFlow. Tanya's flow auto-locks the
 * location to Ottawa (handled downstream by InlineBookingFlow's existing
 * isTanya logic, which we don't touch here).
 */
const StartChoices = ({ isFr, onPick, onProvider, hasUsual }) => {
  const [q, setQ] = useState("");
  const submit = (e) => { e?.preventDefault?.(); const t = q.trim(); if (t) onPick("search", t); };
  const quick = [
    { key: "help", Icon: HelpCircle, label: isFr ? "Consultation gratuite" : "Free consultation" },
    { key: "followup", Icon: RotateCcw, label: isFr ? "Suivi (sans frais)" : "Follow-up (no cost)" },
    { key: "reschedule", Icon: CalendarClock, label: isFr ? "Déplacer ou annuler" : "Reschedule or cancel" },
  ];
  const choices = [
    hasUsual && { key: "usual", Icon: RotateCcw, title: isFr ? "Réserver comme d'habitude" : "Book my usual", sub: isFr ? "Même soin, même clinique — il ne reste qu'à choisir l'heure" : "Same treatment, same clinic — just pick a time", accent: true },
    { key: "find", Icon: Search, title: isFr ? "Trouver un soin" : "Find a treatment", sub: isFr ? "Tous nos soins — par catégorie, nom ou numéro" : "Every treatment we offer — by category, name or number" },
    { key: "provider", Icon: UserRound, title: isFr ? "Réserver avec mon prestataire" : "Book with my provider", sub: isFr ? "Karine, Linda ou Tanya — voir leurs jours et heures" : "Karine, Linda or Tanya — see their days and hours" },
    { key: "help", Icon: HelpCircle, title: isFr ? "Aidez-moi à choisir" : "Help me choose", sub: isFr ? "Consultation gratuite — nous vous guidons vers le bon soin" : "Free consultation — we'll guide you to the right treatment" },
  ].filter(Boolean);
  return (
    <div className="px-4 sm:px-8 md:px-10 pb-7 sm:pb-9 max-w-3xl mx-auto space-y-3" data-testid="start-choices">
      <form onSubmit={submit} className="relative" data-testid="start-search-form">
        <Search className="w-5 h-5 text-zinc-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={isFr ? "Chercher un soin ou un numéro (ex. Botox, #12)…" : "Search a treatment or number (e.g. Botox, #12)…"}
          className="w-full rounded-2xl border-2 border-zinc-200 bg-white pl-12 pr-24 py-4 text-base text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-amber-400 min-h-[60px]"
          aria-label={isFr ? "Chercher un soin" : "Search a treatment"}
          data-testid="start-search-input"
        />
        <button type="submit" disabled={!q.trim()} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl bg-amber-500 disabled:bg-zinc-200 disabled:text-zinc-400 text-zinc-950 font-bold px-4 py-2.5 text-sm min-h-[44px]" data-testid="start-search-submit">
          {isFr ? "Chercher" : "Search"}
        </button>
      </form>
      <div className="grid grid-cols-3 gap-2" data-testid="start-quick-actions">
        {quick.map(({ key, Icon, label }) => (
          <button key={key} type="button" onClick={() => onPick(key)} className="rounded-2xl border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 px-2 py-3 flex flex-col items-center gap-1.5 text-center min-h-[76px] touch-manipulation" data-testid={`start-quick-${key}`}>
            <Icon className="w-5 h-5" />
            <span className="text-xs sm:text-sm font-semibold leading-tight">{label}</span>
          </button>
        ))}
      </div>
      {choices.map(({ key, Icon, title, sub, accent }) => (
        <button
          key={key}
          type="button"
          onClick={() => (key === "provider" ? onProvider() : onPick(key))}
          className={`w-full text-left rounded-2xl border px-5 py-4 sm:py-5 flex items-center gap-4 transition-all touch-manipulation min-h-[76px] ${
            accent ? "bg-amber-500 border-amber-500 text-zinc-950 hover:bg-amber-400" : "bg-white border-zinc-200 hover:border-amber-400 hover:shadow-md text-zinc-900"
          }`}
          data-testid={`start-choice-${key}`}
        >
          <span className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${accent ? "bg-zinc-950/10" : "bg-amber-50 text-amber-700"}`}>
            <Icon className="w-6 h-6" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-base sm:text-lg font-semibold leading-tight">{title}</span>
            <span className={`block text-sm mt-0.5 ${accent ? "text-zinc-900/80" : "text-zinc-600"}`}>{sub}</span>
          </span>
          <ChevronRight className={`w-5 h-5 flex-shrink-0 ${accent ? "text-zinc-900" : "text-amber-500"}`} />
        </button>
      ))}
    </div>
  );
};

const ProviderChooserModal = ({ open, onClose, onPick }) => {
  const [mode, setMode] = useState("start");
  const [hasUsual, setHasUsual] = useState(false);
  useEffect(() => {
    if (!open) return;
    setMode("start");
    try {
      const email = localStorage.getItem("kl_booking_email");
      if (!email) { setHasUsual(false); return; }
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/last-booking?email=${encodeURIComponent(email)}`)
        .then((r) => r.json()).then((d) => setHasUsual(Boolean(d?.found))).catch(() => setHasUsual(false));
    } catch { setHasUsual(false); }
  }, [open]);
  const { language } = useLanguage();
  const isFr = language === "fr";

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const t = isFr
    ? {
        eyebrow: "Réserver un rendez-vous",
        title: "Avec qui souhaitez-vous réserver ?",
        sub: "Choisissez votre clinicien pour voir ses disponibilités et ses traitements.",
        karine: {
          role: "Infirmière autorisée · Injectrice de précision",
          locs: "Ottawa · Kanata · Carleton Place",
          tag: "Recommandé",
          specs: ["Botox", "Fillers", "PRP", "Sculptra", "Tous les soins"],
          cta: "Réserver avec Karine",
        },
        linda: {
          role: "Infirmière praticienne · Ottawa · les jeudis 12 h à 17 h",
          locs: "Ottawa uniquement · les jeudis 12 h – 17 h",
          tag: "IP · Injections",
          specs: ["Botox", "Fillers", "Sculptra", "Consultations"],
          cta: "Réserver avec Linda",
        },
        tanya: {
          role: "Esthéticienne médicale · Ottawa · Lun+Ven 10h-18h",
          locs: "Ottawa uniquement · Lundis & vendredis",
          tag: "Laser · Soins",
          specs: ["Épilation laser", "HydraFacial", "RF Microneedling", "Laser Genesis"],
          cta: "Réserver avec Tanya",
        },
        cancel: "Annuler",
      }
    : {
        eyebrow: "Book an appointment",
        title: "Who would you like to book with?",
        sub: "Choose your clinician to see their schedule and treatment menu.",
        karine: {
          role: "Registered Nurse · Precision Injectionist",
          locs: "Ottawa · Kanata · Carleton Place",
          tag: "Recommended",
          specs: ["Botox", "Dermal Fillers", "PRP", "Sculptra", "All treatments"],
          cta: "Book with Karine",
        },
        linda: {
          role: "Nurse Practitioner · Ottawa · Thursdays 12–5 pm",
          locs: "Ottawa only · Thursdays 12–5 pm",
          tag: "NP · Injections",
          specs: ["Botox", "Dermal Fillers", "Sculptra", "Consultations"],
          cta: "Book with Linda",
        },
        tanya: {
          role: "Medical Aesthetician · Ottawa · Mon+Fri 10-6",
          locs: "Ottawa only · Mondays & Fridays",
          tag: "Laser · Skincare",
          specs: ["Laser Hair Removal", "HydraFacial", "RF Microneedling", "Laser Genesis"],
          cta: "Book with Tanya",
        },
        cancel: "Cancel",
      };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[110] bg-zinc-950/80 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto overscroll-contain"
          onClick={onClose}
          data-testid="provider-chooser-modal"
          style={{ paddingTop: "max(env(safe-area-inset-top), 12px)", paddingBottom: "max(env(safe-area-inset-bottom), 12px)" }}
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-5xl bg-gradient-to-b from-zinc-50 via-white to-amber-50/30 rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20 w-10 h-10 sm:w-9 sm:h-9 rounded-full bg-white/95 hover:bg-white border border-zinc-200 flex items-center justify-center text-zinc-700 shadow-sm transition-all touch-manipulation"
              data-testid="provider-chooser-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Header */}
            <div className="px-5 sm:px-6 md:px-10 pt-7 sm:pt-8 md:pt-10 pb-5 sm:pb-6 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100/70 border border-amber-200/60 text-amber-800 text-[10px] uppercase tracking-[0.18em] font-bold mb-3">
                <Sparkles className="w-3 h-3" />
                {t.eyebrow}
              </div>
              <h2
                className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-zinc-900 mb-2 leading-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {mode === "start" ? (isFr ? "Que souhaitez-vous faire ?" : "What would you like to do?") : t.title}
              </h2>
              <p className="text-xs sm:text-sm text-zinc-600 max-w-md mx-auto">
                {mode === "start" ? (isFr ? "Cherchez un soin, ou choisissez une option ci-dessous." : "Search a treatment, or pick an option below.") : t.sub}
              </p>
              {mode === "provider" && (
                <button type="button" onClick={() => setMode("start")} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-amber-700 hover:underline min-h-[44px] px-2" data-testid="start-choices-back">
                  <ChevronLeft className="w-4 h-4" /> {isFr ? "Retour" : "Back"}
                </button>
              )}
            </div>

            {mode === "start" && (
              <StartChoices isFr={isFr} hasUsual={hasUsual} onPick={(k, extra) => onPick(k, extra)} onProvider={() => setMode("provider")} />
            )}

            {/* Cards */}
            {/* Iter 460 · Feb 2026 · Karine ask — "Clients should easily
                be able to book Tanya or Karine." Tanya (Jackson, Medical
                Aesthetician, Ottawa Mon+Fri 10-6) is now an active
                provider so BOTH cards render side-by-side again. The
                Aug 2026 removal referenced Tanya Shatenko, who is no
                longer with the clinic; Karine hired Tanya Jackson in
                iter 440+ and her booking funnel is fully wired.

                Sep 28 2026 · Karine ask — add Linda Lafrance NP as a
                bookable provider (Ottawa Thursdays only, live Oct 8+).
                Grid promoted to 3 columns on md+ so all three clinicians
                appear side-by-side; on mobile they stack. */}
            {mode === "provider" && <div className="px-3 sm:px-4 md:px-8 pb-6 sm:pb-8 md:pb-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 max-w-6xl mx-auto">
              {/* ===== Karine card ===== */}
              <button
                onClick={() => onPick("karine")}
                data-testid="provider-chooser-karine"
                className="group relative text-left bg-white rounded-2xl border-2 border-zinc-200 hover:border-amber-500 hover:shadow-2xl hover:-translate-y-1 active:scale-[0.98] transition-all duration-300 overflow-hidden touch-manipulation"
              >
                {/* Recommended ribbon */}
                <div className="absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full bg-amber-600 text-white text-[9px] sm:text-[10px] uppercase tracking-[0.15em] font-bold shadow-md flex items-center gap-1">
                  <Crown className="w-2.5 h-2.5" />
                  {t.karine.tag}
                </div>

                <div className="aspect-[4/3] bg-gradient-to-br from-amber-50 to-zinc-100 relative overflow-hidden">
                  <img
                    src="/karine-headshot.jpg"
                    alt="Karine Lauzon, RN"
                    loading="lazy"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/85 via-zinc-900/30 to-transparent" />
                  <div className="absolute bottom-3 left-4 right-4 text-white">
                    <h3 className="font-semibold text-lg sm:text-xl md:text-2xl mb-0.5 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Karine Lauzon, RN
                    </h3>
                    <p className="text-[13px] sm:text-sm font-semibold text-white leading-snug drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]" data-testid="provider-role-karine">{t.karine.role}</p>
                  </div>
                </div>

                <div className="p-4 sm:p-5">
                  <div className="flex items-center gap-1.5 text-sm text-zinc-900 mb-3">
                    <MapPin className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                    <span className="font-medium">{t.karine.locs}</span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {t.karine.specs.map((s) => (
                      <span key={s} className="px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200/60 text-amber-900 text-xs font-semibold">
                        {s}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-zinc-100 min-h-[44px]">
                    <span className="text-amber-700 font-bold text-sm sm:text-base group-hover:text-amber-800">{t.karine.cta}</span>
                    <ArrowRight className="w-4 h-4 text-amber-700 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </button>

              {/* ===== Linda card ===== */}
              <button
                onClick={() => onPick("linda")}
                data-testid="provider-chooser-linda"
                className="group relative text-left bg-white rounded-2xl border-2 border-zinc-200 hover:border-teal-500 hover:shadow-2xl hover:-translate-y-1 active:scale-[0.98] transition-all duration-300 overflow-hidden touch-manipulation"
              >
                {/* NP ribbon */}
                <div className="absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full bg-teal-600 text-white text-[9px] sm:text-[10px] uppercase tracking-[0.15em] font-bold shadow-md flex items-center gap-1">
                  <Droplets className="w-2.5 h-2.5" />
                  {t.linda.tag}
                </div>

                <div className="aspect-[4/3] bg-gradient-to-br from-teal-50 to-zinc-100 relative overflow-hidden">
                  <img
                    src="/team/linda-lafrance.jpg"
                    alt="Linda Lafrance, Nurse Practitioner"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/85 via-zinc-900/30 to-transparent" />
                  <div className="absolute bottom-3 left-4 right-4 text-white">
                    <h3 className="font-semibold text-lg sm:text-xl md:text-2xl mb-0.5 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Linda Lafrance, NP
                    </h3>
                    <p className="text-[13px] sm:text-sm font-semibold text-white leading-snug drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]" data-testid="provider-role-linda">{t.linda.role}</p>
                  </div>
                </div>

                <div className="p-4 sm:p-5">
                  <div className="flex items-center gap-1.5 text-sm text-zinc-900 mb-3">
                    <MapPin className="w-3.5 h-3.5 text-teal-600 flex-shrink-0" />
                    <span className="font-medium">{t.linda.locs}</span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {t.linda.specs.map((s) => (
                      <span key={s} className="px-2 py-0.5 rounded-full bg-teal-50 border border-teal-200/60 text-teal-900 text-xs font-semibold">
                        {s}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-zinc-100 min-h-[44px]">
                    <span className="text-teal-700 font-bold text-sm sm:text-base group-hover:text-teal-800">{t.linda.cta}</span>
                    <ArrowRight className="w-4 h-4 text-teal-700 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </button>

              {/* ===== Tanya card ===== */}
              <button
                onClick={() => onPick("tanya")}
                data-testid="provider-chooser-tanya"
                className="group relative text-left bg-white rounded-2xl border-2 border-zinc-200 hover:border-sky-500 hover:shadow-2xl hover:-translate-y-1 active:scale-[0.98] transition-all duration-300 overflow-hidden touch-manipulation"
              >
                {/* Specialist ribbon */}
                <div className="absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full bg-sky-600 text-white text-[9px] sm:text-[10px] uppercase tracking-[0.15em] font-bold shadow-md flex items-center gap-1">
                  <Zap className="w-2.5 h-2.5" />
                  {t.tanya.tag}
                </div>

                <div className="aspect-[4/3] bg-gradient-to-br from-sky-50 to-zinc-100 relative overflow-hidden">
                  <img
                    src="https://customer-assets-lqy194kg.emergentagent.net/job_8776705b-3965-48ce-aa62-68a5febfba36/artifacts/fijnziro_6264D407-44BC-4A1F-A2C7-329361C8339A.png"
                    alt="Tanya Jackson, Medical Aesthetician"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/85 via-zinc-900/30 to-transparent" />
                  <div className="absolute bottom-3 left-4 right-4 text-white">
                    <h3 className="font-semibold text-lg sm:text-xl md:text-2xl mb-0.5 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Tanya Jackson
                    </h3>
                    <p className="text-[13px] sm:text-sm font-semibold text-white leading-snug drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]" data-testid="provider-role-tanya">{t.tanya.role}</p>
                  </div>
                </div>

                <div className="p-4 sm:p-5">
                  <div className="flex items-center gap-1.5 text-sm text-zinc-900 mb-3">
                    <MapPin className="w-3.5 h-3.5 text-sky-600 flex-shrink-0" />
                    <span className="font-medium">{t.tanya.locs}</span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {t.tanya.specs.map((s) => (
                      <span key={s} className="px-2 py-0.5 rounded-full bg-sky-50 border border-sky-200/60 text-sky-900 text-xs font-semibold">
                        {s}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-zinc-100 min-h-[44px]">
                    <span className="text-sky-700 font-bold text-sm sm:text-base group-hover:text-sky-800">{t.tanya.cta}</span>
                    <ArrowRight className="w-4 h-4 text-sky-700 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </button>
            </div>}

            {/* Footer cancel link */}
            <div className="px-6 pb-6 text-center">
              <button
                onClick={onClose}
                data-testid="provider-chooser-cancel"
                className="text-xs text-zinc-500 hover:text-zinc-700 underline underline-offset-2 transition-colors"
              >
                {t.cancel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProviderChooserModal;
